<template>
  <div :class="metaBlockWrapperClassName" :style="wrappingReadSettingsStyles">
    <div :class="metaBlockClassName" :dir="metaView.direction">
      <div class="meta-info-layout">
        <div class="meta-info-left-control pt-6"></div>
        <div class="flex-shrink-0 pt-6 meta-info-content">
          <div>
            <div class="meta-info-summary">
              <div class="flex-grow-0 pa-0">
                <div class="book-image-box">
                  <BookCover
                    :book-id="metaView.id"
                    :cover-size="thumbSize.XLARGE"
                  />
                  <PublicationProgress :publication-id="metaView.id" />
                </div>
              </div>
              <div class="meta-info-details-wrapper">
                <div class="meta-info-details">
                  <div class="book-title">
                    {{ metaView.name }}
                  </div>
                  <AuthorLinkControl
                    v-if="metaView.author"
                    class="mt-3"
                    :publication="metaView"
                  />
                  <div v-if="metaView.category" class="book-category mt-3">
                    {{ $t('BookMetaBlock.category.label') }}
                    <nuxt-link
                      class="book-category-link"
                      :to="toCategoryLink"
                      >{{ metaView.category }}</nuxt-link
                    >
                  </div>
                  <div
                    v-if="metaView.genres && metaView.genres.length"
                    class="book-genres mt-3"
                  >
                    {{ $t('BookMetaBlock.genres.label') }}
                    <nuxt-link
                      v-for="(genre, index) in metaView.genres"
                      :key="genre"
                      class="book-genre-link"
                      :to="getGenreRout(genre)"
                      >{{
                        `${genre}${
                          index === metaView.genres.length - 1 ? '' : ','
                        }`
                      }}
                    </nuxt-link>
                  </div>
                  <div class="meta-info-stats-wrapper my-3">
                    <div class="meta-info-stats">
                      <div class="meta-info-stats-list">
                        <span
                          v-if="showDifficulty && metaView.difficulty"
                          class="ma-0"
                        >
                          {{
                            $t('Library.book.item.level', {
                              level: metaView.difficulty
                            })
                          }}
                        </span>
                        <span class="ma-0">
                          <TimeDuration
                            :duration="metaView.readingTime"
                            dir="auto"
                          />
                        </span>
                        <span v-if="bookSize" class="ma-0">{{ bookSize }}</span>
                        <span v-if="metaView.audio" class="ma-0">
                          <BaseSpriteIcon
                            icon-name="ico-headset-wire"
                          ></BaseSpriteIcon>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="meta-info-controls-wrapper">
                    <div class="pa-0 flex-grow-1">
                      <ControlsMetaBlock
                        :book-id="metaView.id"
                        :audio="metaView.audio"
                        :book-name="metaView.name"
                        :is-content-ready="isContentReady"
                        @controlsMetaBlockEvent="controlsMetaBlockEventHandler"
                      >
                        <template #custom-control>
                          <ManageFavouritesControl
                            outlined
                            block
                            x-large
                            :publication-id="metaView.id"
                          >
                          </ManageFavouritesControl>
                        </template>
                      </ControlsMetaBlock>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div class="px-0 pt-6 pb-14">
                <div
                  v-if="metaView.showDescription"
                  class="meta-info-description"
                  :style="descriptionStyles"
                >
                  {{ metaView.description }}
                </div>
              </div>
            </div>
            <div v-if="bookSeo" class="book-meta-tabs-wrapper">
              <BookMetaTabs
                ref="bookMetaTabs"
                :book-seo="bookSeo"
                :meta-view="metaView"
                @tabChanged="handleTabChanged"
              />
            </div>
          </div>
        </div>
        <div class="meta-info-right-control pt-6 d-flex"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { sizes as thumbSize } from '@shared/enums/ThumbSizesEnum';
import AppConstantsUtil from '@/services/utils/AppConstantsUtil';
import Utils from '@/services/utils/Utils';
import { encodeStringToPath } from '@shared/utils/url';
import AppStateEnum from '@/enums/AppStateEnum';

import PublicationProgress from '@/components/views/PublicationProgress/PublicationProgress';
import TimeDuration from '@/components/base/TimeDuration/TimeDuration';
import ControlsMetaBlock from '@/components/views/MetaBlock/ControlsMetaBlock/ControlsMetaBlock';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon';
import BookCover from '@/components/base/BookCover/BookCover';
import AuthorLinkControl from '@/components/controls/AuthorLinkControl/AuthorLinkControl.vue';
import ManageFavouritesControl from '@/components/controls/ManageFavouritesControl/ManageFavouritesControl';
import BookMetaTabs from './BookMetaTabs.vue';

export default {
  name: 'BookMetaBlock',
  components: {
    PublicationProgress,
    ControlsMetaBlock,
    TimeDuration,
    BaseSpriteIcon,
    BookCover,
    AuthorLinkControl,
    ManageFavouritesControl,
    BookMetaTabs
  },
  props: {
    metaView: {
      type: Object,
      required: true
    },
    wrappingReadSettingsStyles: {
      type: Array,
      required: false,
      default: function() {
        return [];
      }
    },
    readSettingsStyles: {
      type: Object,
      required: false,
      default: function() {
        return {};
      }
    },
    descriptionStyles: {
      type: Object,
      required: false,
      default: function() {
        return {};
      }
    },
    isContentReady: {
      type: Boolean,
      required: true
    }
  },
  data() {
    const showDifficulty = this.$store.getters['ContextStore/showDifficulty'];
    return {
      showDifficulty,
      metaBlockClassName: AppConstantsUtil.META_BLOCK_CLASS,
      metaBlockWrapperClassName: AppConstantsUtil.META_BLOCK_WRAPPER_CLASS,
      thumbSize
    };
  },

  computed: {
    isOnline() {
      return this.$store.getters['ContextStore/isOnline'];
    },
    bookSeo() {
      return this.$store.getters['PublicationStore/getBookSeo'];
    },
    bookSize() {
      return (
        this.metaView?.mediaSize &&
        Utils.convertBytesToProperSize(this.metaView?.mediaSize)
      );
    },
    toCategoryLink() {
      return this.$store.getters['LibraryStore/toCategoryLink'](this.metaView);
    }
  },
  methods: {
    controlsMetaBlockEventHandler(payload) {
      const { type, data } = payload;

      if (type === 'play-button-clicked') {
        this.handlePlayButtonClicked();
        return;
      }

      this.$_emitEvent(type, data);
    },

    handlePlayButtonClicked() {
      const currentTab = this.$refs.bookMetaTabs?.activeTab;

      if (
        currentTab &&
        ['brief-intro', 'about-author', 'faq'].includes(currentTab)
      ) {
        this.$nextTick(() => {
          this.$refs.bookMetaTabs.handleTabChange('book');
        });
      }
    },

    getGenreRout(genre) {
      return {
        name: AppStateEnum.GENRE_PAGE,
        params: {
          name: encodeStringToPath(genre),
          genreSlug: encodeStringToPath(genre)
        }
      };
    },
    handleTabChanged(tabData) {
      this.$_emitEvent('tabChanged', tabData);
    },
    $_emitEvent(type, data = {}) {
      this.$emit('metablockEvent', {
        type,
        data
      });
    }
  }
};
</script>
